import { useEffect, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ClientCustomerType } from '@econtractor/types';
import * as apiClient from 'api/apiClients';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import SolarIcon from 'components/icons/SolarIcon';
import SvgTrash from 'components/svg/SvgTrash';
import type { NewObjectType } from 'modules/lead';
import type { NewSaleType } from 'modules/objects';
import { type ClientCustomerActionClient } from './ClientCustomerAction';
import ClientCustomersTable from './ClientCustomersTable';
import OffClientCustomerEdit from './OffClientCustomerEdit';

interface OffClientCustomersProps {
    clientId: number | null;
    onHide: () => void;
    client?: ClientCustomerActionClient;
    onCreateSale?: (sale: NewSaleType) => void;
    onCreateObject?: (object: NewObjectType) => void;
}

const OffClientCustomers = ({
    clientId,
    onHide,
    client,
    onCreateSale,
    onCreateObject,
}: OffClientCustomersProps) => {
    const { t } = useTranslation(['Clients', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const [list, setList] = useState<ClientCustomerType[] | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [editCustomer, setEditCustomer] = useState<ClientCustomerType | null>(null);
    const [isCreate, setIsCreate] = useState(false);
    const [formNonce, setFormNonce] = useState(0);

    const show = clientId !== null;

    const fetchList = async () => {
        if (!clientId) return;
        const res = await apiClient.getClientCustomers(clientId);
        setShowSpinner(false);
        if (!res) {
            showToast(t('common:messError'), 'danger');
            setList((prev) => prev ?? []);
            return;
        }
        setList(res);
    };

    const handleClose = () => {
        setIsCreate(false);
        setEditCustomer(null);
        setList(null);
        onHide();
    };

    const handleHideEdit = () => {
        setIsCreate(false);
        setEditCustomer(null);
    };

    const handleDelete = (row: ClientCustomerType) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            if (!clientId) return;
            setShowSpinner(true);
            const stat = await apiClient.deleteClientCustomer(clientId, row.ID);
            await fetchList();
            setShowSpinner(false);
            if (stat) showToast(t('subjects.messDeleted'), 'success');
            else showToast(t('common:messError'), 'danger');
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        <SvgTrash className="text-danger" />
                        <span className="ps-2">{t('subjects.confirmDelete')}</span>
                    </h4>
                    <h5 className="mb-0">{row.fullName || row.edrpouOrIpn}</h5>
                    <small className="form-text text-muted">{row.edrpouOrIpn}</small>
                </>
            ),
        });
    };

    useEffect(() => {
        if (!clientId) return;
        setList(null);
        setIsCreate(false);
        setEditCustomer(null);
        void fetchList();
    }, [clientId]);

    return (
        <>
            <Offcanvas show={show} onHide={handleClose} placement="end" className="off-client-customers">
                <Offcanvas.Header className="border-bottom" closeButton>
                    <Offcanvas.Title className="me-auto">{t('subjects.title')}</Offcanvas.Title>
                    
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-coming-form d-flex flex-column h-100">
                        <div className="off-coming-content px-3 pb-3">
                            {list == null ? (
                                <ESpinner />
                            ) : (<>
                                <ClientCustomersTable
                                    rows={list}
                                    showSpinner={showSpinner}
                                    onEdit={(row) => {
                                        setIsCreate(false);
                                        setFormNonce((n) => n + 1);
                                        setEditCustomer(row);
                                    }}
                                    onDelete={handleDelete}
                                    onCreateSale={onCreateSale}
                                    onCreateObject={onCreateObject}
                                    client={client}
                                />
                                <div className="d-flex justify-content-center mt-3">
                                    <Button
                                        type="button"
                                        variant="info"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => {
                                            setEditCustomer(null);
                                            setFormNonce((n) => n + 1);
                                            setIsCreate(true);
                                        }}
                                    >
                                        <SolarIcon name="plus" className="me-1" />
                                        {t('subjects.toolAdd')}
                                    </Button>
                                </div></>
                            )}
                            
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {clientId ? (
                <OffClientCustomerEdit
                    clientId={clientId}
                    customer={editCustomer}
                    isCreate={isCreate}
                    formNonce={formNonce}
                    onHide={handleHideEdit}
                    onSaved={async () => {
                        setShowSpinner(true);
                        await fetchList();
                    }}
                />
            ) : null}
        </>
    );
};

export default OffClientCustomers;
